import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, getDocs } from "firebase/firestore";
import { db } from '../firebase';

const Admin = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { currentUser, isReady } = useAdminGuard();

  const [dataReady, setDataReady] = useState(false);
  const [players, setPlayers] = useState([])
  const [teams, setTeams] = useState([])

  const SEASON = config.currentSeason;

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      const loadedTeams = await getTeams();
      await getPlayers(loadedTeams);
      setDataReady(true);
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTeams = async () => {
    const [teamsSnapshot, seasonTeamsSnapshot] = await Promise.all([
      getDocs(collection(db, "teams")),
      getDocs(collection(db, "seasonTeams"))
    ]);

    const teamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const seasonTeamsData = seasonTeamsSnapshot.docs
      .map(doc => ({ seasonTeamID: doc.id, ...doc.data() }))
      .filter(st => st.season == SEASON);

    const mergedTeams = seasonTeamsData.map(st => {
      let team = teamsData.find(t => t.id === st.teamID);
      return team ? { ...st, ...team } : null;
    }).filter(Boolean);

    setTeams(mergedTeams);
    return mergedTeams;
  }

  async function getPlayers(teams) {
    const playerSnapshot = await getDocs(collection(db, 'players'));
    const players = playerSnapshot.docs.map((doc) => doc.data());

    const playerIDsInSeason = teams.map(t => t.players).flat()
    const filteredPlayers = players.filter(p => playerIDsInSeason.includes(p.id))

    setPlayers(filteredPlayers)
    return players
  }

  async function copyAllEmails() {
    const emails = players.map(player => player.email).join(', ');
    navigator.clipboard.writeText(emails)
      .then(() => alert('Emails copied to clipboard!'))
      .catch(err => console.error('Failed to copy emails: ', err));
  }

  async function downloadAllData() {
    const collectionNames = [
      'articles', 'comments', 'config', 'draft', 'gameStats', 'games',
      'players', 'playerViews', 'prayer_intentions', 'records', 'seasons',
      'seasonTeams', 'signups', 'teams'
    ];

    const snapshots = await Promise.all(
      collectionNames.map(name => getDocs(collection(db, name)))
    );

    const fullData = {};
    collectionNames.forEach((name, i) => {
      fullData[name] = snapshots[i].docs.map(doc => ({ id: doc.id, ...doc.data() }));
    });

    const today = new Date();
    const pad = n => String(n).padStart(2, '0');
    const formattedDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}_${pad(today.getHours())}-${pad(today.getMinutes())}-${pad(today.getSeconds())}`;
    const filename = `league_data_${formattedDate}.json`;

    const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (dataReady == false || currentUser == null) {
    return (
      <div className="Account">
        <LoadingAnimation />
      </div>
    )
  } else {
    return (
      <div>
        <div className="league-content">

          <div className="league-body">
            <p className="league-title-standard-secondary text-align-center">Choose an Admin Action</p>

            <div className="league-table-container schedule medium-item-buttons">
              <button className="btn btn-dark" type="button" onClick={downloadAllData}>Download All Data</button>
              <button className="btn btn-dark" type="button" onClick={copyAllEmails}>Copy all Player Emails</button>
            </div>

            <div className="league-table-container schedule large-item-buttons-admin">
              <div className="large-item-button" onClick={() => navigate('/admin/games')}>
                <h2>Game</h2>
                <p>Enter stats for game</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/teams')}>
                <h2>Team</h2>
                <p>Edit team data</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/seasons')}>
                <h2>Season</h2>
                <p>Edit season data</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/players')}>
                <h2>Player</h2>
                <p>Edit player data</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/signups')}>
                <h2>Signups</h2>
                <p>Manage player signups</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/draft')}>
                <h2>Draft</h2>
                <p>Manage season draft</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/config')}>
                <h2>Config</h2>
                <p>Edit league settings</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/tools')}>
                <h2>Tools</h2>
                <p>Data utilities</p>
              </div>
              <div className="large-item-button" onClick={() => navigate('/admin/awards')}>
                <h2>Awards</h2>
                <p>Manage badges and awards</p>
              </div>
            </div>
          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default Admin;
